import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCheck, Calendar, ShoppingCart } from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useFetch } from '../hooks';
import { dashboardService } from '../services/api';
import { formatCurrency } from '../utils/helpers';

const COLORS  = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
const COLORS2 = ['#10b981', '#6b7280', '#ef4444'];

const StatCard = ({ title, value, sub, icon: Icon, color, onClick }) => {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  };
  return (
    <div onClick={onClick} className={`card p-4 flex items-center gap-3 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
      <div className={`p-2.5 rounded-xl shrink-0 ${colors[color] || colors.blue}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{title}</p>
        <p className="text-xl font-bold text-gray-900 dark:text-white">{value ?? '—'}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
};

const SectionCard = ({ title, children }) => (
  <div className="card p-4 space-y-3">
    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</p>
    {children}
  </div>
);

const Row = ({ label, value, color = 'text-gray-900 dark:text-white' }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className={`font-semibold ${color}`}>{value ?? '—'}</span>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { data: stats, loading } = useFetch(() => dashboardService.getStats());

  if (loading) return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {Array(8).fill(0).map((_, i) => <div key={i} className="card h-24 animate-pulse bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
    </div>
  );

  const d = stats || {};

  const apptPieData = [
    { name: 'Pending',   value: Number(d.appointments?.pending)   || 0 },
    { name: 'Upcoming',  value: Number(d.appointments?.upcoming)  || 0 },
    { name: 'Completed', value: Number(d.appointments?.completed) || 0 },
    { name: 'Cancelled', value: Number(d.appointments?.cancelled) || 0 },
    { name: 'Rejected',  value: Number(d.appointments?.rejected)  || 0 },
  ];

  const medicineOrdersBarData = [
    { name: 'Placed',       value: Number(d.medicine_orders?.placed)           || 0 },
    { name: 'Confirmed',    value: Number(d.medicine_orders?.confirmed)         || 0 },
    { name: 'Out Delivery', value: Number(d.medicine_orders?.out_for_delivery)  || 0 },
    { name: 'Delivered',    value: Number(d.medicine_orders?.delivered)         || 0 },
    { name: 'Cancelled',    value: Number(d.medicine_orders?.cancelled)         || 0 },
  ];

  const labOrdersBarData = [
    { name: "Requested", value: Number(d.lab_test_orders?.requested) || 0 },
    { name: "Accepted", value: Number(d.lab_test_orders?.accepted) || 0 },
    { name: "Completed", value: Number(d.lab_test_orders?.completed) || 0 },
    { name: "Cancelled", value: Number(d.lab_test_orders?.cancelled) || 0 },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Welcome back! Here's what's happening with MediHub today.</p>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Total Patients"  value={d.users?.total}           sub={`${d.users?.active || 0} active`}           icon={Users}        color="blue"   onClick={() => navigate('/patients')} />
        <StatCard title="Total Doctors"   value={d.doctors?.total}         sub={`${d.doctors?.verified || 0} verified`}      icon={UserCheck}    color="green"  onClick={() => navigate('/doctors')} />
        <StatCard title="Appointments"    value={d.appointments?.total}    sub={`${d.appointments?.pending || 0} pending`}   icon={Calendar}     color="purple" onClick={() => navigate('/appointments')} />
        <StatCard title="Medicine Orders" value={d.medicine_orders?.total} sub={`${d.medicine_orders?.placed || 0} placed`}  icon={ShoppingCart} color="orange" onClick={() => navigate('/orders')} />
      </div>

      {/* Earnings + Withdrawals + Appointments Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Earnings">
          <Row label="Total Billed"       value={formatCurrency(d.earnings?.total_billed)}       color="text-green-600 dark:text-green-400" />
          <Row label="Total Commission"   value={formatCurrency(d.earnings?.total_commission)}   color="text-blue-600 dark:text-blue-400" />
          <Row label="Pending Commission" value={formatCurrency(d.earnings?.pending_commission)} color="text-yellow-600 dark:text-yellow-400" />
        </SectionCard>

        <SectionCard title="Withdrawals">
          <Row label="Total"           value={d.withdrawals?.total} />
          <Row label="Pending"         value={d.withdrawals?.pending}          color="text-yellow-600 dark:text-yellow-400" />
          <Row label="Completed"       value={d.withdrawals?.completed}        color="text-green-600 dark:text-green-400" />
          <Row label="Total Withdrawn" value={formatCurrency(d.withdrawals?.total_withdrawn)} color="text-blue-600 dark:text-blue-400" />
          <Row label="Pending Amount"  value={formatCurrency(d.withdrawals?.pending_amount)}  color="text-red-500" />
        </SectionCard>

        {/* Appointments Pie */}
        <div className="card p-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Appointments by Status</p>
          {apptPieData.some(i => i.value > 0) ? (
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={apptPieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value">
                  {apptPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend iconSize={8} iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 text-center py-10">No data</p>
          )}
        </div>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Medicine Orders Bar Chart */}
        <div className="card p-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Medicine Orders Breakdown</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={medicineOrdersBarData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {medicineOrdersBarData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Lab Orders Bar Chart */}
        <div className="card p-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Lab Test Orders Breakdown</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={labOrdersBarData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {labOrdersBarData.map((_, i) => (
                  <Cell key={i} fill={['#8b5cf6', '#3b82f6', '#10b981', '#ef4444'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Medicines Pie + Support Tickets + bottom cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Medicines Stock Status">
          <Row label="Total"        value={d.medicines?.total} />
          <Row label="Active"       value={d.medicines?.active}       color="text-green-600 dark:text-green-400" />
          <Row label="Inactive"     value={d.medicines?.inactive}     color="text-gray-400" />
          <Row label="Out of Stock" value={d.medicines?.out_of_stock} color="text-red-500" />
        </SectionCard>

        <SectionCard title="Support Tickets">
          <Row label="Total"    value={d.support_tickets?.total} />
          <Row label="Pending"  value={d.support_tickets?.pending}  color="text-yellow-600 dark:text-yellow-400" />
          <Row label="Resolved" value={d.support_tickets?.resolved} color="text-green-600 dark:text-green-400" />
          <Row label="Rejected" value={d.support_tickets?.rejected} color="text-red-500" />
        </SectionCard>

        <SectionCard title="Doctors Overview">
          <Row label="Total"    value={d.doctors?.total} />
          <Row label="Active"   value={d.doctors?.active}   color="text-green-600 dark:text-green-400" />
          <Row label="Inactive" value={d.doctors?.inactive} color="text-gray-400" />
          <Row label="Verified" value={d.doctors?.verified} color="text-blue-600 dark:text-blue-400" />
        </SectionCard>
      </div>
    </div>
  );
};

export default Dashboard;
